import styled, { keyframes, css } from "styled-components";

// ─── Keyframes ──────────────────────────────────────────────────────────────

const fadeSlideOut = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(-8px) scale(0.97); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// ─── Overlay & Container ────────────────────────────────────────────────────

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  backdrop-filter: blur(12px);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  transition: opacity 0.3s ease-in-out;
  opacity: ${(p) => (p.$isAnimating ? 1 : 0)};
`;

export const ModalContainer = styled.div`
  width: 100%;
  max-width: 500px;
  position: absolute;
  top: 50%;
  left: 50%;
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  transform: ${({ $isAnimating }) =>
    $isAnimating
      ? "translate(-50%, -50%) scale(1)"
      : "translate(-50%, -50%) scale(0.88) translateY(40px)"};
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);

  border-radius: 24px;
  border: 1px solid #183052;
  background: #0b1727;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.6),
    0 0 0 0.5px rgba(255, 255, 255, 0.04) inset;

  @media (max-width: 550px) {
    width: calc(100% - 32px);
  }
`;

// ─── Layout ─────────────────────────────────────────────────────────────────

export const ModalContent = styled.div`
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

export const ChevronRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transform: rotate(180deg);
`;

export const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 1px;
  margin: 2px 0;
`;

export const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// ─── Header elements ────────────────────────────────────────────────────────

export const ModalTitle = styled.h2`
  color: #fff;
  font-family: Figtree;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.3px;
  transition: all 0.25s ease;
`;

export const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  opacity: 0.6;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

export const ChevronBack = styled.button`
  background: rgba(255, 255, 255, 0);
  border: none;
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;
  transition: all 0.2s ease;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: ${(p) => (p.$visible ? "translateX(0)" : "translateX(-6px)")};

  &:hover {
    background: rgba(255, 255, 255, 0);
    transform: translateX(-1px);
  }
`;

// ─── Options list ────────────────────────────────────────────────────────────

export const OptionsWrapper = styled.div`
  display: grid;
  grid-template-rows: ${(p) => (p.$collapsed ? "0fr" : "1fr")};
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
  transition:
    grid-template-rows 0.38s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.28s ease;

  & > div {
    /* overflow: hidden; */
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

export const OptionCard = styled.div`
  border-radius: 14px;
  background: #142743;
  border: 1px solid transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 14px;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  position: relative;
  overflow: hidden;

  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;

  ${(p) =>
    !p.$disabled &&
    css`
      &:hover {
        border-color: rgba(255, 255, 255, 0.12);
        background: #172e4e;
        transform: translateY(-1px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
      }

      /* Shimmer line on hover */
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(
          105deg,
          transparent 40%,
          rgba(255, 255, 255, 0.04) 50%,
          transparent 60%
        );
        background-size: 200% 100%;
        opacity: 0;
        transition: opacity 0.2s ease;
      }

      &:hover::after {
        opacity: 1;
        animation: ${shimmer} 0.7s ease forwards;
      }
    `}

  /* Hiding animation when a different option is selected */
  ${(p) =>
    p.$hiding &&
    css`
      animation: ${fadeSlideOut} 0.22s ease forwards;
      pointer-events: none;
    `}
`;

export const ModalTextRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: flex-start;

  svg {
    width: 18px !important;
    height: 18px !important;
    path {
      stroke: ${(p) => (p.$disabled ? "#2877d980" : "#2877d9")};
      stroke-width: 1.5px;
    }
  }
`;

export const EmojiIcon = styled.span`
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
`;

export const OptionTitle = styled.p`
  color: ${(p) => (p.$disabled ? "rgba(255,255,255,0.4)" : "#fff")};
  font-family: Figtree;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

export const OptionDescription = styled.p`
  color: ${(p) =>
    p.$disabled ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.8)"};
  font-family: Figtree;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
`;

export const OptionRequires = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-family: Figtree;
  font-size: 13px;
  font-weight: 300;
  line-height: 17px;
  margin-top: -2px;
`;

// ─── Expanded / form area ────────────────────────────────────────────────────

export const ExpandedContent = styled.div`
  display: grid;
  grid-template-rows: ${(p) => (p.$visible ? "1fr" : "0fr")};
  opacity: ${(p) => (p.$visible ? 1 : 0)};

  transition:
    grid-template-rows 0.42s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.32s ease;

  & > div {
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-bottom: 2px; /* prevent clipping on last element */
  }

  ${(p) =>
    p.$visible &&
    css`
      & > div > * {
        animation: ${fadeIn} 0.35s ease both;
      }
      & > div > *:nth-child(1) {
        animation-delay: 0.08s;
      }
      & > div > *:nth-child(2) {
        animation-delay: 0.13s;
      }
      & > div > *:nth-child(3) {
        animation-delay: 0.17s;
      }
      & > div > *:nth-child(4) {
        animation-delay: 0.2s;
      }
      & > div > *:nth-child(5) {
        animation-delay: 0.23s;
      }
      & > div > *:nth-child(6) {
        animation-delay: 0.26s;
      }
      & > div > *:nth-child(7) {
        animation-delay: 0.28s;
      }
      & > div > *:nth-child(8) {
        animation-delay: 0.3s;
      }
      & > div > *:nth-child(9) {
        animation-delay: 0.32s;
      }
      & > div > *:nth-child(10) {
        animation-delay: 0.34s;
      }
    `}
`;

export const SelectedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

// ─── Text ────────────────────────────────────────────────────────────────────

export const ModalText = styled.p`
  color: #fff;
  font-family: Figtree;
  font-size: 14px;
  font-weight: ${(p) => (p.$bold ? "600" : "400")};
  cursor: ${(p) => (p.$click ? "pointer" : "default")};
  line-height: 20px;
  font-style: ${(p) => (p.$italic ? "italic" : "normal")};
  opacity: ${(p) => (p.$bold ? 1 : 0.85)};

  &:hover {
    text-decoration: ${(p) => (p.$click ? "underline" : "none")};
  }
`;

// ─── Input ───────────────────────────────────────────────────────────────────

export const InputLabel = styled.label`
  color: rgba(255, 255, 255, 0.6);
  font-family: Figtree;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: -4px;
`;

export const ModelNameInput = styled.input`
  border-radius: 12px;
  border: 1px solid #23406a;
  background: #0e1c31;
  height: 44px;
  padding: 0 14px;
  width: 100%;
  box-sizing: border-box;

  color: #fff;
  font-family: Figtree;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.2px;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.04);
  }
`;

// ─── Legacy / unused (kept for compatibility) ────────────────────────────────

export const ModalTextContainer = styled.div`
  border-radius: 10px;
  background: #142743;
  display: flex;
  width: 100%;
  padding: 16px 12px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
`;
